import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Keeps a stable media lock per product+color for the current page lifecycle.
 * This avoids image drift after filters morph/re-render product cards.
 * @type {Map<string, string>}
 */
const colorMediaLockMap = new Map();

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];
  /** @type {MutationObserver | undefined} */
  #cardItemObserver;
  /** @type {string | undefined} */
  #initialPriceVariantId;
  #handleCollectionFiltersRendered = () => {
    this.#initializeCardVariant();
    [120].forEach((delay) => {
      setTimeout(() => this.#initializeCardVariant(), delay);
    });
  };

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    if (checkedInput?.dataset.variantId) {
      return checkedInput.dataset.variantId;
    }

    const cardItem = this.closest('[data-variant-id]');
    if (cardItem instanceof HTMLElement && cardItem.dataset.variantId) {
      return cardItem.dataset.variantId;
    }

    return null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    if (this.#isColorLockedCard()) {
      this.removeAttribute('data-variant-image-ready');
      // Try to lock media as early as possible on first paint.
      requestAnimationFrame(() => {
        this.#initializeCardVariant();
      });
      // Safety fallback: never leave cards blank if a locked media id cannot be resolved.
      setTimeout(() => {
        if (!this.hasAttribute('data-variant-image-ready')) {
          this.#initializeCardVariant();
          this.#markVariantImageReady();
        }
      }, 100);
    }

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);
    document.addEventListener('collection:filters-rendered', this.#handleCollectionFiltersRendered);

    const cardItem = this.closest('[data-variant-id]');
    if (cardItem) {
      this.#cardItemObserver = new MutationObserver((mutations) => {
        const shouldReinitialize = mutations.some(
          (mutation) =>
            mutation.type === 'attributes' &&
            (mutation.attributeName === 'data-variant-id' ||
              mutation.attributeName === 'data-color-value')
        );

        if (shouldReinitialize) {
          this.#initializeCardVariant();
        }
      });

      this.#cardItemObserver.observe(cardItem, {
        attributes: true,
        attributeFilter: ['data-variant-id', 'data-color-value'],
      });
    }

    this.#initializeCardVariant();
    // Some refs (slideshow/swatches) mount after connectedCallback in dynamic grids.
    // Retry a few times so split cards reliably lock to their intended variant media.
    [120].forEach((delay) => {
      setTimeout(() => this.#initializeCardVariant(), delay);
    });

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  /**
   * Public hook used after section morph/filter updates to force card state sync.
   */
  refreshCardState() {
    if (this.#forceLockedCardState()) return;
    this.#initializeCardVariant();
  }

  #applyLockedColorTitle() {
    if (!this.#isColorLockedCard()) return;

    const cardItem = this.closest('[data-color-value]');
    const colorValue = cardItem?.getAttribute('data-color-value')?.trim();
    if (!colorValue) return;

    const titleLink = this.refs.productTitleLink;
    if (!(titleLink instanceof HTMLAnchorElement)) return;

    const baseTitle = titleLink.dataset.baseTitle?.trim() || titleLink.textContent?.trim();
    if (!baseTitle) return;

    const finalTitle = `${baseTitle} - ${colorValue}`;
    const titleNode = titleLink.querySelector('.title-text, p, span');

    if (titleNode instanceof HTMLElement) {
      if (titleNode.textContent?.trim() !== finalTitle) {
        titleNode.textContent = finalTitle;
      }
    } else if (titleLink.textContent?.trim() !== finalTitle) {
      titleLink.textContent = finalTitle;
    }
  }

  #markVariantImageReady() {
    if (this.#isColorLockedCard() && !this.hasAttribute('data-variant-image-ready')) {
      this.setAttribute('data-variant-image-ready', 'true');
    }
  }

  /**
   * Applies strict locked variant/media state for split color cards.
   * @returns {boolean} true when a locked state was applied.
   */
  #forceLockedCardState() {
    if (!this.#isColorLockedCard()) return false;

    const cardItem = this.closest('[data-variant-id]');
    const lockedVariantId = cardItem?.getAttribute('data-variant-id');
    if (!lockedVariantId) return false;

    let lockedMediaId = this.#getLockedMediaId();
    if (!lockedMediaId) {
      const mediaMapNode = /** @type {HTMLElement | null} */ (
        this.querySelector(`.card-gallery__variant-media-map [data-variant-id="${CSS.escape(lockedVariantId)}"]`)
      );
      lockedMediaId = mediaMapNode?.dataset.mediaId || null;
      if (lockedMediaId && cardItem?.getAttribute('data-initial-media-id') !== lockedMediaId) {
        cardItem?.setAttribute('data-initial-media-id', lockedMediaId);
      }
    }

    this.#updateCardUrlsWithVariant(lockedVariantId);
    this.#syncQuickAddVariant(lockedVariantId);

    const lockedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`input[data-variant-id="${CSS.escape(lockedVariantId)}"]`)
    );
    if (lockedInput && !lockedInput.checked) {
      lockedInput.checked = true;
    }

    const didSelectVariantMedia = this.#selectVariantMediaById(lockedVariantId);
    let didLockMedia = false;
    if (lockedMediaId) {
      didLockMedia = this.#selectMediaById(lockedMediaId, true);
    }

    if (didLockMedia || didSelectVariantMedia || !lockedMediaId) {
      this.#markVariantImageReady();
    }

    this.#applyLockedColorTitle();

    this.#previousSlideIndex = null;
    return true;
  }

  /**
   * Initializes the card to the variant specified by the parent grid item.
   * This is used for split variant cards where each card represents one color.
   */
  #initializeCardVariant() {
    const cardItem = this.closest('[data-variant-id]');
    const initialVariantId = cardItem?.getAttribute('data-variant-id');
    const initialMediaId = cardItem?.getAttribute('data-initial-media-id');
    const initialColorValue = cardItem?.getAttribute('data-color-value');
    if (!initialVariantId) return;

    this.#updateInitialVariantPrice(initialVariantId, initialColorValue);

    const productId = cardItem?.getAttribute('data-product-id') || '';
    const colorKey = initialColorValue ? `${productId}|${initialColorValue.trim().toLowerCase()}` : '';
    const lockedMediaId = colorKey ? colorMediaLockMap.get(colorKey) : null;
    const effectiveMediaId = lockedMediaId || initialMediaId;

    if (colorKey && effectiveMediaId) {
      colorMediaLockMap.set(colorKey, effectiveMediaId);
      if (cardItem?.getAttribute('data-initial-media-id') !== effectiveMediaId) {
        cardItem?.setAttribute('data-initial-media-id', effectiveMediaId);
      }
    }

    /** @type {HTMLInputElement | null} */
    let targetInput = this.querySelector(`input[data-variant-id="${CSS.escape(initialVariantId)}"]`);

    if (!targetInput && effectiveMediaId) {
      targetInput = /** @type {HTMLInputElement | null} */ (
        this.querySelector(`input[data-option-media-id="${CSS.escape(effectiveMediaId)}"]`)
      );
    }

    if (!targetInput && initialColorValue) {
      const swatchInputs = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('input[name$="-swatch"]'));
      targetInput = Array.from(swatchInputs).find((input) => input.value?.trim() === initialColorValue.trim()) || null;
    }

    if (targetInput) {
      if (!targetInput.checked) {
        targetInput.checked = true;
        if (!this.#isColorLockedCard()) {
          targetInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        // Ensure media updates even if no swatch picker listeners are mounted.
        this.#updateCardUrlsWithVariant(initialVariantId);
        this.#syncQuickAddVariant(initialVariantId);
        const didSelectVariantMedia = this.#selectVariantMediaById(initialVariantId);
        const didSelectMedia = this.#selectMediaById(effectiveMediaId, this.#isColorLockedCard());
        if (didSelectMedia || didSelectVariantMedia || this.#isColorLockedCard() == false) {
          this.#markVariantImageReady();
        }
        this.#applyLockedColorTitle();
      } else {
        this.#updateCardUrlsWithVariant(initialVariantId);
        this.#syncQuickAddVariant(initialVariantId);
        const didSelectVariantMedia = this.#selectVariantMediaById(initialVariantId);
        const didSelectMedia = this.#selectMediaById(effectiveMediaId, this.#isColorLockedCard());
        if (didSelectMedia || didSelectVariantMedia || this.#isColorLockedCard() == false) {
          this.#markVariantImageReady();
        }
        this.#applyLockedColorTitle();
      }
      return;
    }

    this.#updateCardUrlsWithVariant(initialVariantId);
    this.#syncQuickAddVariant(initialVariantId);
    const didSelectVariantMedia = this.#selectVariantMediaById(initialVariantId);
    const didSelectMedia = this.#selectMediaById(effectiveMediaId, this.#isColorLockedCard());
    if (didSelectMedia || didSelectVariantMedia || this.#isColorLockedCard() == false) {
      this.#markVariantImageReady();
    }
    this.#applyLockedColorTitle();

    // If slideshow/swatches are not ready yet, retry once more shortly.
    if (!this.refs.slideshow) {
      setTimeout(() => this.#initializeCardVariant(), 400);
    }
  }

  /**
   * Loads the price for a split-color card's exact initial variant.
   * @param {string} variantId
   * @param {string | null | undefined} colorValue
   */
  #updateInitialVariantPrice(variantId, colorValue) {
    if (!colorValue || this.#initialPriceVariantId === variantId) return;

    const productUrl = this.refs.productCardLink?.href;
    if (!productUrl) return;

    const requestUrl = new URL(productUrl, window.location.origin);
    requestUrl.searchParams.set('variant', variantId);
    requestUrl.searchParams.set('section_id', 'section-rendering-product-card');
    this.#initialPriceVariantId = variantId;

    fetch(requestUrl.toString())
      .then((response) => (response.ok ? response.text() : null))
      .then((responseText) => {
        if (!responseText) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const currentPrice = this.querySelector('[ref="priceContainer"]');
        const updatedPrice = html.querySelector('[ref="priceContainer"]');

        if (currentPrice && updatedPrice) {
          morph(currentPrice, updatedPrice);
        }
      })
      .catch(() => {
        this.#initialPriceVariantId = undefined;
      });
  }

  /**
   * Returns true when this card is a split color card with a locked image identity.
   * @returns {boolean}
   */
  #isColorLockedCard() {
    const cardItem = this.closest('[data-variant-id]');
    return Boolean(cardItem?.getAttribute('data-color-value'));
  }

  /**
   * Gets the locked media id from the card wrapper.
   * @returns {string | null}
   */
  #getLockedMediaId() {
    const cardItem = this.closest('[data-variant-id]');
    return cardItem?.getAttribute('data-initial-media-id') || null;
  }

  /**
   * Selects a slideshow slide directly by media id.
   * @param {string | null | undefined} mediaId
   * @param {boolean} [lockAllSlides]
   * @returns {boolean}
   */
  #selectMediaById(mediaId, lockAllSlides = false) {
    const { slideshow } = this.refs;
    if (!slideshow || !mediaId) return false;

    const { slides = [] } = slideshow.refs || {};
    const hasTargetSlide = slides.some((slide) => slide.getAttribute('slide-id') === mediaId);
    if (!hasTargetSlide) return false;

    for (const slide of slides) {
      if (!lockAllSlides && slide.getAttribute('variant-image') == null) continue;
      slide.hidden = slide.getAttribute('slide-id') !== mediaId;
    }

    slideshow.select({ id: mediaId }, undefined, { animate: false });
    return true;
  }

  /**
   * Selects the variant-specific media slide based on variant id.
   * @param {string} variantId
   * @returns {boolean}
   */
  #selectVariantMediaById(variantId) {
    const { slideshow } = this.refs;
    if (!slideshow) return false;

    const mediaMapNode = /** @type {HTMLElement | null} */ (
      this.querySelector(`.card-gallery__variant-media-map [data-variant-id="${CSS.escape(variantId)}"]`)
    );
    const mediaId = mediaMapNode?.dataset.mediaId;
    if (!mediaId) return false;

    const { slides = [] } = slideshow.refs || {};
    for (const slide of slides) {
      if (slide.getAttribute('variant-image') == null) continue;
      slide.hidden = slide.getAttribute('slide-id') !== mediaId;
    }

    slideshow.select({ id: mediaId }, undefined, { animate: false });
    return true;
  }

  /**
   * Ensures product card links include the selected variant query parameter.
   * @param {string} variantId
   */
  #updateCardUrlsWithVariant(variantId) {
    const { productCardLink, cardGalleryLink, productTitleLink } = this.refs;
    const links = [productCardLink, cardGalleryLink, productTitleLink];

    for (const link of links) {
      if (!(link instanceof HTMLAnchorElement) || !link.href) continue;

      const url = new URL(link.href);
      url.searchParams.set('variant', variantId);
      link.href = url.toString();
    }
  }

  /**
   * Syncs quick-add hidden variant id with the card's selected variant.
   * @param {string} variantId
   */
  #syncQuickAddVariant(variantId) {
    const quickAddVariantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('quick-add-component input[ref="variantId"]')
    );

    if (quickAddVariantInput) {
      quickAddVariantInput.value = variantId;
      quickAddVariantInput.disabled = false;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    document.removeEventListener('collection:filters-rendered', this.#handleCollectionFiltersRendered);
    this.#cardItemObserver?.disconnect();
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();
    this.updatePrice(event);

    if (this.#forceLockedCardState()) {
      this.removeAttribute('data-no-swatch-selected');
      this.#updateOverflowList();
      return;
    }

    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    if (this.#isColorLockedCard()) {
      const cardItem = this.closest('[data-variant-id]');
      const lockedVariantId = cardItem?.getAttribute('data-variant-id');
      const lockedMediaId = this.#getLockedMediaId();

      if (lockedVariantId) {
        this.#updateCardUrlsWithVariant(lockedVariantId);
        this.#syncQuickAddVariant(lockedVariantId);
        this.#selectVariantMediaById(lockedVariantId);
      }

      if (lockedMediaId) {
        this.#selectMediaById(lockedMediaId, true);
      }

      this.#previousSlideIndex = null;
      this.removeAttribute('data-no-swatch-selected');
      this.#updateOverflowList();
      return;
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceElement = this.querySelector('[ref="priceContainer"]');
    const newPriceElement = event.detail.data.html.querySelector('[ref="priceContainer"]');

    if (newPriceElement && priceElement) {
      morph(priceElement, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    const lockedMediaId = this.#getLockedMediaId();

    if (this.#isColorLockedCard() && lockedMediaId) {
      this.#selectMediaById(lockedMediaId, true);
      return;
    }

    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (this.#isColorLockedCard()) {
      return;
    }

    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    const lockedMediaId = this.#getLockedMediaId();
    if (this.#isColorLockedCard() && lockedMediaId) {
      this.#selectMediaById(lockedMediaId, true);
      return;
    }

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const lockedMediaId = this.#getLockedMediaId();
    if (this.#isColorLockedCard() && lockedMediaId) {
      this.#selectMediaById(lockedMediaId, true);
      return;
    }

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const lockedMediaId = this.#getLockedMediaId();
    if (this.#isColorLockedCard() && lockedMediaId) {
      this.#selectMediaById(lockedMediaId, true);
      return;
    }

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
